import type { UserId } from "@nimble/datamodel/types/userId";
import { useQuery } from "@tanstack/react-query";
import { create, windowScheduler } from "@yornaath/batshit";

import { getMatchStatus } from "@/lib/api-clients/recommendation-api-client";

export function getMatchStatusQueryKey(userId: UserId, ncesId: string) {
  return ["match-status", userId, ncesId] as const;
}

type MatchStatusRequest = {
  userId: UserId;
  ncesId: string;
};

const batchedFetcher = create({
  fetcher: async (items: MatchStatusRequest[]) => {
    const firstItem = items[0];

    if (!firstItem) return [];

    const { userId } = firstItem;
    const ncesIds = items.map((item) => item.ncesId);
    const { data } = await getMatchStatus(userId, {
      school_nces_ids: ncesIds,
    });
    return data;
  },
  resolver: (data, item) => data.find((d) => d.school_nces_id === item.ncesId),
  scheduler: windowScheduler(10),
});

export default function useMatchStatus({
  ncesId,
  userId,
}: {
  ncesId: string;
  userId: UserId;
}) {
  return useQuery({
    queryKey: getMatchStatusQueryKey(userId, ncesId),
    queryFn: () => batchedFetcher.fetch({ userId, ncesId }),
    retry: false,
  });
}
